import { FileTextIcon, DownloadIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../tooltip';
import { Button } from '../../button';
import { DropZone } from './drop-zone';

export function DocumentUpload(props: {
	id?: string;
	currentUrl: string | undefined;
	currentKey: string | undefined;
	file: File | undefined;
	onChange: (file: File | undefined) => void;
	onRemove: () => void;
	label: string;
	readOnly: boolean;
	noFileText?: string;
	fallbackFileName?: string;
	downloadText?: string;
	removeFileText?: string;
	dropZoneDescription?: string;
}) {
	const hasExisting = !!(props.currentUrl || props.currentKey);
	const hasFile = props.file || hasExisting;
	const fileName = props.file?.name ?? (props.currentKey ? props.currentKey.split('/').pop() : undefined);

	return (
		<div>
			<div
				className="grid"
				style={{
					gridTemplateRows: props.readOnly && !hasFile ? '1fr' : '0fr',
					opacity: props.readOnly && !hasFile ? 1 : 0,
					transition: 'grid-template-rows 200ms, opacity 200ms',
				}}
			>
				<div className="overflow-hidden">
					<p className="text-muted-foreground text-sm">{props.noFileText ?? 'No file uploaded'}</p>
				</div>
			</div>
			<AnimatePresence initial={false}>
				{hasFile && (
					<motion.div
						key="file-info"
						initial={{ height: 0 }}
						animate={{ height: 'auto' }}
						exit={{ height: 0 }}
						transition={{ type: 'spring', visualDuration: 0.2, bounce: 0 }}
						className="overflow-hidden"
					>
						<div
							className={`flex items-center gap-3 rounded-xl border border-[#e4e4e4] px-4 py-3 ${!props.readOnly ? 'mb-3' : ''}`}
						>
							<div className="rounded-lg bg-[#e1e9e7] p-2">
								<FileTextIcon className="size-4 text-[#5C7972]" />
							</div>
							<span className="min-w-0 flex-1 truncate text-sm font-medium">
								{fileName ?? props.fallbackFileName ?? 'Bank Account Certificate'}
							</span>
							<div className="flex shrink-0 items-center gap-1">
								{props.currentUrl && (
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<a
													href={props.currentUrl}
													target="_blank"
													rel="noopener noreferrer"
													aria-label={props.downloadText ?? 'Download'}
													className="text-muted-foreground hover:text-foreground rounded-lg p-1 transition-colors"
												>
													<DownloadIcon className="size-4" />
												</a>
											</TooltipTrigger>
											<TooltipContent>{props.downloadText ?? 'Download'}</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								)}
								{!props.readOnly && (
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													aria-label={props.removeFileText ?? 'Remove file'}
													onClick={() => {
														props.onChange(undefined);
														props.onRemove();
													}}
												>
													<XIcon className="size-4" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>{props.removeFileText ?? 'Remove file'}</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								)}
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
			<div
				className="grid"
				style={{
					gridTemplateRows: props.readOnly ? '0fr' : '1fr',
					opacity: props.readOnly ? 0 : 1,
					transition: 'grid-template-rows 200ms, opacity 200ms',
				}}
			>
				<div className="-m-1 overflow-hidden p-1">
					<DropZone
						id={props.id}
						accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
						label={props.label}
						description={props.dropZoneDescription}
						onFiles={([file]) => {
							if (file) props.onChange(file);
						}}
					/>
				</div>
			</div>
		</div>
	);
}
